// Add-widget coach card. Shows once per user (on Today) on iOS native.
// Dismissed permanently when user taps "Got it" — backed by the
// `widget_coach_dismissed` boolean in profiles.
import { Capacitor } from '@capacitor/core';
import { useUserPrefs } from '../contexts/UserPrefsContext';
import { Button } from './ui/button';
import { Sparkles, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const WidgetCoach = () => {
  const { prefs, setPref, loaded } = useUserPrefs();
  const isNative = Capacitor?.isNativePlatform?.() === true;
  const platform = (() => { try { return Capacitor?.getPlatform?.(); } catch { return 'web'; } })();
  // iOS only for now — Android widget add-flow is different.
  const show = isNative && platform === 'ios' && loaded && !prefs.widget_coach_dismissed;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.25 }}
          data-testid="widget-coach-card"
          className="rounded-2xl border border-primary/30 bg-primary/5 p-4 sm:p-5 relative"
        >
          <button
            type="button"
            onClick={() => setPref('widget_coach_dismissed', true)}
            className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Dismiss"
            data-testid="widget-coach-dismiss"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-start gap-3 pr-6">
            <Sparkles className="h-5 w-5 mt-0.5 text-primary shrink-0" />
            <div className="space-y-2">
              <div className="font-heading text-base font-medium">Add the My Flow Habit widget</div>
              <p className="text-sm text-muted-foreground">
                Glance at your habits from your home screen — no app to open.
              </p>
              <ol className="text-xs text-muted-foreground space-y-0.5 leading-relaxed">
                <li>1. Long-press an empty spot on your home screen.</li>
                <li>2. Tap the <span className="font-medium text-foreground">+</span> in the top corner.</li>
                <li>3. Search <span className="font-medium text-foreground">My Flow Habit</span>, pick a size, then tap <span className="font-medium text-foreground">Add Widget</span>.</li>
                <li>4. Choose layout (Habits or Quote) in Settings → Home-screen widget.</li>
              </ol>
              <div className="pt-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-full text-xs h-7 px-3"
                  onClick={() => setPref('widget_coach_dismissed', true)}
                  data-testid="widget-coach-got-it"
                >
                  Got it
                </Button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
